import React, { useCallback, useMemo, useState } from 'react';
import { NetworkStatus, useQuery } from '@apollo/client';
import capitalize from 'lodash/capitalize';
import {
  useSidebar,
  FormattedDate,
  getGSACBaseURL,
  DynamicResultTable,
  StyledTitle,
  DismissableInlineMessage,
  NoDataContentInTable,
} from '@css-xp/base';
import { Link } from 'react-router-dom';
import { EmailConnection, PageInfo, EmailEdge, Email } from '@css-xp/graphtypes';
import { DocumentNode } from 'graphql';
import { GloballyAllowedSidebar } from '@atlassian/cssxp-utils-generic';
import { formatDateStringFromGraphql } from '@atlassian/cssxp-utils-graph';
import EmailIcon from '@atlaskit/icon/glyph/email';
import AttachmentIcon from '@atlaskit/icon/glyph/attachment';
import { DEFAULT_ROWS_PER_PAGE, DEFAULT_PAGE } from '@atlassian/cssxp-utils-entitlement';
// import EmailsMissingDisclaimer from '@atlassian/cssxp-utils-graph'; TO:DO

export interface EmailTabProps {
  id: string;
  entityName: string;
  query: DocumentNode;
}

const columns = [
  {
    name: 'Subject',
    heading: false,
    customClassNames: 'col-30',
  },
  {
    name: '',
    customClassNames: 'col-10',
  },
  {
    name: 'To',
    customClassNames: 'col-20',
  },
  {
    name: 'From',
    customClassNames: 'col-20',
  },
  {
    name: 'Date',
    customClassNames: 'col-20',
  },
];

export const EmailTab = ({ id, entityName, query }: EmailTabProps) => {
  entityName = entityName.toLowerCase();
  const [rowsPerPage, setRowsPerPage] = useState<number>(DEFAULT_ROWS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState<number>(DEFAULT_PAGE);
  const { data, error, loading, refetch } = useQuery(query, {
    variables: { id: id, searchOptions: { first: rowsPerPage, offset: 0 } },
    displayName: `fetch${capitalize(entityName)}Emails`,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: !id,
  });

  const { openSidebar } = useSidebar();
  const results = data?.entitlementDetails;
  const emailsConnection: EmailConnection = results?.emails;
  const emailEdges = emailsConnection?.edges || [];
  const totalHits = emailsConnection?.totalCount ?? 0;

  const headData = useMemo(() => {
    const cells = [
      {
        content: 'Subject',
        width: 30,
      },
      {
        content: '',
        width: 10,
      },
      {
        content: 'To',
        width: 20,
      },
      {
        content: 'From',
        width: 15,
      },
      {
        content: 'Date',
        width: 15,
      },
    ];

    return {
      cells,
    };
  }, []);

  const showEmptyMailMessage = () => {
    return (
      <DismissableInlineMessage
        position="left-end"
        renderMessage={() => (
          <p className="inline-message">
            Here’s where you’ll see billing emails and some ticket comms. For comprehensive ticket history, check in
            <Link
              target="_blank"
              to={{ pathname: `${getGSACBaseURL()}/api/1/redirects/gsac/jql` }}
              data-testid={'email-gsac-url'}
            >
              {' GSAC '}
            </Link>
            .
          </p>
        )}
        renderTitle={(toggleMessage: () => void) => (
          <span className="hoverable-icon" onClick={toggleMessage}>
            <StyledTitle title="Can’t find the emails you’re looking for?" type="info" size="medium" />
          </span>
        )}
      />
    );
  };

  const emptyStateOrderProp = {
    header: '',
    description: 'No emails found',
  };

  const onPageChange = useCallback(
    (newPage: number) => {
      setCurrentPage(newPage);
      refetch({
        searchOptions: {
          first: rowsPerPage,
          offset: newPage !== 1 ? newPage * rowsPerPage - rowsPerPage : 0,
        },
      });
    },
    [rowsPerPage, refetch]
  );
  const hasAttachments = (email: any) => {
    return email.attachments && email.attachments.length > 0;
  };

  const createRows = (): Array<any> => {
    const result = (emailEdges ?? []).map(
      //@ts-ignore
      (emailEdge: EmailEdge, index: number) => {
        const email: Email = emailEdge.node;
        const { subject, to, from, date, id } = email;
        const rowPayload = {
          emailId: id,
        };
        return {
          cells: [
            {
              content: (
                <>
                  <div className="email-table-subject-cell">
                    <div className="email-table-email-icon">
                      <EmailIcon label="email-icon" size="medium" />
                    </div>
                    <div>
                      <Link
                        to={`/emails/${id}`}
                        data-testid="email-link"
                        onClick={(e) => e.stopPropagation()}
                        style={{ verticalAlign: 'super' }}
                      >
                        {subject || 'No subject'}
                      </Link>
                    </div>
                  </div>
                </>
              ),
            },
            {
              content: (
                <span>
                  {' '}
                  {hasAttachments(email) && <AttachmentIcon label="" size="medium" data-testid="email-attachment" />}
                </span>
              ),
            },
            {
              content: to ? to[0]?.address : <NoDataContentInTable />,
            },
            {
              content: from ? from[0]?.address : <NoDataContentInTable />,
            },
            {
              content: date ? <FormattedDate date={formatDateStringFromGraphql(date)} /> : <NoDataContentInTable />,
            },
          ],
          onClick: () =>
            openSidebar({
              sidebarType: GloballyAllowedSidebar.EMAIL_PREVIEW,
              payload: rowPayload,
              sidebarOptions: {
                isOverlay: true,
                sidebarWidth: '600px',
              },
            }),
        };
      }
    );
    return result;
  };

  const handleRowsChange = useCallback((rowsCount: number) => {
    setRowsPerPage(rowsCount);
    setCurrentPage(1);
  }, []);

  return (
    <div className="emails-tab" data-testid={`${entityName}-emails`}>
      <DynamicResultTable
        testId={`${entityName}-emails`}
        headingText="Emails"
        head={headData}
        rows={createRows()}
        emptyStateProps={emptyStateOrderProp}
        isLoading={loading}
        totalCount={totalHits}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        isPaginationEnabled={emailEdges && emailEdges?.length > 0 && totalHits > DEFAULT_ROWS_PER_PAGE}
        onRowChange={handleRowsChange}
      />
      <div>{showEmptyMailMessage()}</div>
    </div>
  );
};
